import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  FileText, 
  Eye, 
  Trash2, 
  Copy, 
  ExternalLink,
  Code,
  Users,
  Calendar,
  Edit,
  ToggleLeft,
  ToggleRight,
  X,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Building2,
  Webhook,
  ArrowLeft
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import * as api from '../services/api';
import FormBuilder from './forms/FormBuilder';

const PublicFormsPage = ({ currentUser, globalSearch = '' }) => {
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // View mode: 'list' | 'create' | 'edit'
  const [viewMode, setViewMode] = useState('list');
  
  // Modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Current form being edited/viewed
  const [currentForm, setCurrentForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [embedCode, setEmbedCode] = useState(null);
  
  // Form builder state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    form_type: 'lead_capture',
    fields: [],
    submit_button_text: 'Enviar',
    success_message: '¡Gracias! Tu información ha sido recibida.',
    redirect_url: '',
    primary_color: '#D4A843',
    logo_url: '',
    background_color: '#ffffff',
    auto_create_empresa: true,
    auto_create_contacto: true,
    auto_assign_to: '',
    auto_create_task: false,
    task_title_template: 'Seguimiento: {empresa_name}',
    notification_emails: [],
    is_active: true,
    webhook_url: '',
    webhook_enabled: false
  });
  
  const [saving, setSaving] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [formsRes, templatesRes, usersRes] = await Promise.all([
        api.getForms(),
        api.getFormTemplates(),
        api.getUsers()
      ]);
      setForms(formsRes.data || []);
      setTemplates(templatesRes.data?.templates || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error loading forms data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create new form
  const handleCreateForm = () => {
    setFormData({
      name: '',
      description: '',
      form_type: 'lead_capture',
      fields: [],
      submit_button_text: 'Enviar',
      success_message: '¡Gracias! Tu información ha sido recibida.',
      redirect_url: '',
      primary_color: '#D4A843',
      logo_url: '',
      background_color: '#ffffff',
      auto_create_empresa: true,
      auto_create_contacto: true,
      auto_assign_to: currentUser?.name || '',
      auto_create_task: false,
      task_title_template: 'Seguimiento: {empresa_name}',
      notification_emails: [],
      is_active: true,
      webhook_url: '',
      webhook_enabled: false
    });
    setCurrentForm(null);
    setViewMode('create');
  };

  // Load template fields
  const handleTemplateSelect = (templateType) => {
    const template = templates.find(t => t.type === templateType);
    if (template) {
      const enableAutoTask = ['lead_capture', 'quote_request'].includes(templateType);
      
      setFormData(prev => ({
        ...prev,
        form_type: templateType,
        fields: template.fields.map((f, idx) => ({
          ...f,
          id: f.id || `field_${idx}_${Date.now()}`
        })),
        auto_create_task: enableAutoTask,
        task_title_template: enableAutoTask ? 'Seguimiento: {empresa_name}' : prev.task_title_template
      }));
    }
  };

  // Save form
  const handleSaveForm = async () => {
    if (!formData.name.trim()) {
      alert('El nombre del formulario es requerido');
      return;
    }
    if (formData.fields.length === 0) {
      alert('Agrega al menos un campo al formulario');
      return;
    }

    setSaving(true);
    try {
      if (viewMode === 'edit' && currentForm) {
        await api.updateForm(currentForm.id, formData);
      } else {
        await api.createForm(formData);
      }
      await loadData();
      setViewMode('list');
    } catch (error) {
      console.error('Error saving form:', error);
      let errorMsg = 'Error desconocido';
      if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      alert('Error al guardar el formulario: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Edit form
  const handleEditForm = (form) => {
    setCurrentForm(form);
    setFormData({
      name: form.name || '',
      description: form.description || '',
      form_type: form.form_type || 'lead_capture',
      fields: form.fields || [],
      submit_button_text: form.submit_button_text || 'Enviar',
      success_message: form.success_message || '¡Gracias! Tu información ha sido recibida.',
      redirect_url: form.redirect_url || '',
      primary_color: form.primary_color || '#D4A843',
      logo_url: form.logo_url || '',
      background_color: form.background_color || '#ffffff',
      auto_create_empresa: form.auto_create_empresa ?? true,
      auto_create_contacto: form.auto_create_contacto ?? true,
      auto_assign_to: form.auto_assign_to || '',
      auto_create_task: form.auto_create_task || false,
      task_title_template: form.task_title_template || 'Seguimiento: {empresa_name}',
      notification_emails: form.notification_emails || [],
      is_active: form.is_active ?? true,
      webhook_url: form.webhook_url || '',
      webhook_enabled: form.webhook_enabled || false
    });
    setViewMode('edit');
  };

  // Delete form
  const handleDeleteForm = async () => {
    if (!currentForm) return;
    try {
      await api.deleteForm(currentForm.id);
      await loadData();
      setShowDeleteConfirm(false);
      setCurrentForm(null);
    } catch (error) {
      console.error('Error deleting form:', error);
      alert('Error al eliminar el formulario');
    }
  };

  // Toggle form active status
  const handleToggleActive = async (form) => {
    try {
      await api.updateForm(form.id, { is_active: !form.is_active });
      await loadData();
    } catch (error) {
      console.error('Error toggling form status:', error);
    }
  };

  // View submissions
  const handleViewSubmissions = async (form) => {
    setCurrentForm(form);
    try {
      const res = await api.getFormSubmissions(form.id);
      setSubmissions(res.data || []);
      setShowSubmissionsModal(true);
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  // Get embed code
  const handleGetEmbedCode = async (form) => {
    setCurrentForm(form);
    try {
      const baseUrl = window.location.origin;
      const res = await api.getFormEmbedCode(form.id, baseUrl);
      setEmbedCode(res.data);
      setShowEmbedModal(true);
    } catch (error) {
      console.error('Error getting embed code:', error);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Preview form
  const handlePreview = (form) => {
    setCurrentForm(form);
    setShowPreviewModal(true);
  };

  // Filter forms by search
  const filteredForms = forms.filter(form => 
    !globalSearch || 
    form.name?.toLowerCase().includes(globalSearch.toLowerCase()) ||
    form.description?.toLowerCase().includes(globalSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#D4A843]" size={32} />
      </div>
    );
  }

  // Show Form Builder
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="p-6 space-y-4" data-testid="form-builder-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => setViewMode('list')}
              className="gap-2"
            >
              <ArrowLeft size={18} />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {viewMode === 'edit' ? 'Editar Formulario' : 'Nuevo Formulario'}
              </h1>
              {viewMode === 'create' && templates.length > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-500">Comenzar con plantilla:</span>
                  <Select onValueChange={handleTemplateSelect}>
                    <SelectTrigger className="w-48 h-8 text-sm">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.type} value={t.type}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setViewMode('list')}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveForm}
              className="bg-[#D4A843] hover:bg-[#c49a3a]"
              disabled={saving}
              data-testid="save-form-btn"
            >
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              {viewMode === 'edit' ? 'Guardar Cambios' : 'Crear Formulario'}
            </Button>
          </div>
        </div>

        {/* Form Builder Component */}
        <FormBuilder
          formData={formData}
          setFormData={setFormData}
          onSave={handleSaveForm}
          saving={saving}
          isEdit={viewMode === 'edit'}
        />
      </div>
    );
  }

  // List View
  return (
    <div className="p-6 space-y-6" data-testid="public-forms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Formularios Públicos</h1>
          <p className="text-gray-500 text-sm mt-1">
            Crea formularios para capturar leads desde tu sitio web o redes sociales
          </p>
        </div>
        <Button 
          onClick={handleCreateForm}
          className="bg-[#D4A843] hover:bg-[#c49a3a] text-white"
          data-testid="create-form-btn"
        >
          <Plus size={18} className="mr-2" />
          Nuevo Formulario
        </Button>
      </div>

      {/* Forms Grid */}
      {filteredForms.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="mx-auto text-gray-300 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            {globalSearch ? 'No se encontraron formularios' : 'No hay formularios'}
          </h3>
          <p className="text-gray-500 mb-6">
            {globalSearch 
              ? 'Intenta con otra búsqueda' 
              : 'Crea tu primer formulario para comenzar a capturar leads'
            }
          </p>
          {!globalSearch && (
            <Button 
              onClick={handleCreateForm}
              className="bg-[#D4A843] hover:bg-[#c49a3a] text-white"
            >
              <Plus size={18} className="mr-2" />
              Crear Formulario
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredForms.map(form => (
            <div 
              key={form.id} 
              className={`bg-white rounded-lg border ${form.is_active ? 'border-gray-200' : 'border-red-200 bg-red-50'} p-5 hover:shadow-md transition-shadow`}
              data-testid={`form-card-${form.id}`}
            >
              {/* Form Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: form.primary_color || '#D4A843' }}
                  >
                    <FileText className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{form.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      form.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {form.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleActive(form)}
                  className="text-gray-400 hover:text-gray-600"
                  title={form.is_active ? 'Desactivar' : 'Activar'}
                >
                  {form.is_active ? <ToggleRight size={24} className="text-green-500" /> : <ToggleLeft size={24} />}
                </button>
              </div>

              {/* Form Description */}
              {form.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{form.description}</p>
              )}

              {/* Form Stats */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <Users size={14} />
                  <span>{form.submission_count || 0} envíos</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  <span>{new Date(form.created_at).toLocaleDateString('es-ES')}</span>
                </div>
                {form.webhook_enabled && (
                  <div className="flex items-center gap-1 text-orange-500" title="Webhook n8n activo">
                    <Webhook size={14} />
                    <span>n8n</span>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePreview(form)}
                  className="flex-1"
                  data-testid={`preview-form-${form.id}`}
                >
                  <Eye size={14} className="mr-1" />
                  Vista Previa
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditForm(form)}
                  className="flex-1"
                  data-testid={`edit-form-${form.id}`}
                >
                  <Edit size={14} className="mr-1" />
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleGetEmbedCode(form)}
                  title="Obtener código para embeber"
                >
                  <Code size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewSubmissions(form)}
                  title="Ver envíos"
                >
                  <Users size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => { setCurrentForm(form); setShowDeleteConfirm(true); }}
                  title="Eliminar"
                  data-testid={`delete-form-${form.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Vista Previa: {currentForm?.name}</DialogTitle>
            <DialogDescription className="sr-only">Vista previa del formulario público</DialogDescription>
          </DialogHeader>
          <div 
            className="flex-1 overflow-y-auto p-6 rounded-lg"
            style={{ backgroundColor: currentForm?.background_color || '#ffffff' }}
          >
            {currentForm?.logo_url && (
              <div className="text-center mb-6">
                <img src={currentForm.logo_url} alt="Logo" className="h-12 mx-auto" />
              </div>
            )}
            <h2 className="text-xl font-bold mb-2" style={{ color: currentForm?.primary_color }}>
              {currentForm?.name}
            </h2>
            {currentForm?.description && (
              <p className="text-gray-600 mb-6">{currentForm.description}</p>
            )}
            <div className="space-y-4">
              {currentForm?.fields?.map(field => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.field_type === 'textarea' ? (
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder={field.placeholder}
                      rows={3}
                      disabled
                    />
                  ) : field.field_type === 'select' ? (
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md" disabled>
                      <option>{field.placeholder || 'Seleccionar...'}</option>
                      {field.options?.map((opt, i) => (
                        <option key={i}>{opt}</option>
                      ))}
                    </select>
                  ) : field.field_type === 'checkbox' ? (
                    <div className="flex items-center gap-2">
                      <input type="checkbox" disabled />
                      <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
                    </div>
                  ) : (
                    <input
                      type={field.field_type}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder={field.placeholder}
                      disabled
                    />
                  )}
                  {field.help_text && (
                    <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                  )}
                </div>
              ))}
            </div>
            <button
              className="w-full mt-6 py-3 rounded-md text-white font-medium"
              style={{ backgroundColor: currentForm?.primary_color || '#D4A843' }}
              disabled
            >
              {currentForm?.submit_button_text || 'Enviar'}
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Cerrar
            </Button>
            <Button 
              onClick={() => {
                const url = `${window.location.origin}/f/${currentForm?.slug || currentForm?.id}`;
                window.open(url, '_blank');
              }}
              className="bg-[#D4A843] hover:bg-[#c49a3a]"
            >
              <ExternalLink size={16} className="mr-2" />
              Abrir en Nueva Pestaña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Code Modal */}
      <Dialog open={showEmbedModal} onOpenChange={setShowEmbedModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="text-[#D4A843]" size={20} />
              Código para Embeber
            </DialogTitle>
            <DialogDescription className="sr-only">Código de integración para tu sitio web</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Direct URL */}
            <div>
              <label className="text-sm font-semibold">URL Directa del Formulario</label>
              <div className="flex gap-2 mt-2">
                <Input value={embedCode?.form_url || ''} readOnly className="bg-gray-50" />
                <Button 
                  variant="outline" 
                  onClick={() => copyToClipboard(embedCode?.form_url)}
                  className="shrink-0"
                >
                  {copySuccess ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16} />}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.open(embedCode?.form_url, '_blank')}
                  className="shrink-0"
                >
                  <ExternalLink size={16} />
                </Button>
              </div>
            </div>

            {/* iFrame Code */}
            <div>
              <label className="text-sm font-semibold">Código iFrame (Recomendado)</label>
              <p className="text-xs text-gray-500 mb-2">
                Copia y pega este código en tu sitio web donde quieras mostrar el formulario
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                  {embedCode?.iframe_code}
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(embedCode?.iframe_code)}
                >
                  <Copy size={14} className="mr-1" />
                  Copiar
                </Button>
              </div>
            </div>

            {/* Script Code */}
            <div>
              <label className="text-sm font-semibold">Código Script (Avanzado)</label>
              <p className="text-xs text-gray-500 mb-2">
                Para mayor control sobre el estilo y comportamiento
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                  {embedCode?.script_code}
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(embedCode?.script_code)}
                >
                  <Copy size={14} className="mr-1" />
                  Copiar
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmbedModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions Modal */}
      <Dialog open={showSubmissionsModal} onOpenChange={setShowSubmissionsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-[#D4A843]" size={20} />
              Envíos: {currentForm?.name}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({submissions.length} total)
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">Historial de envíos del formulario</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {submissions.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No hay envíos para este formulario</p>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map(sub => (
                  <div key={sub.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="text-sm text-gray-500">
                        <span className="font-medium text-gray-700">
                          {new Date(sub.submitted_at).toLocaleString('es-ES')}
                        </span>
                        {sub.source && (
                          <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {sub.source}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {sub.created_empresa_id && (
                          <span className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-0.5 rounded">
                            <Building2 size={12} />
                            Empresa creada
                          </span>
                        )}
                        {sub.created_task_id && (
                          <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                            <CheckCircle size={12} />
                            Tarea creada
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(sub.data || {}).map(([key, value]) => (
                        <div key={key} className="text-sm">
                          <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="ml-1 text-gray-800">{value || '-'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmissionsModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Eliminar Formulario
            </DialogTitle>
            <DialogDescription className="sr-only">Confirmación para eliminar el formulario</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              ¿Estás seguro de eliminar el formulario <strong>{currentForm?.name}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Esta acción no se puede deshacer. Los envíos existentes se mantendrán pero el formulario ya no aceptará nuevos.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteForm}
              data-testid="confirm-delete-form"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicFormsPage;
